import { formatDisplayDate } from '@/utils/date'
/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-27 10:43:55
 * @ Description: Table showing all of the property files
 */

import React, { useState } from 'react'
import Pagination from '@mui/material/Pagination'

// Centralized lodash imports - much cleaner!
import {
  map,
  find,
  ceil,
  slice,
  get,
  isString,
  every,
  isNil,
} from '@/utils/lodash'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Table,
  TableCell,
  TableSortLabel,
  Tooltip,
  Box,
} from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import withSession from '@/housefolios-components/Session/withSession'
import { enqueueSnackbar } from 'notistack'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import FileUpload from '@/housefolios-components/PropertyContainer/PropertyDashboard/FileUpload'
import Add from '@mui/icons-material/Add'
import {
  strategyIconGap10Style,
  strategyFlexSelfCenterSx,
  strategyScrollXTouchSx,
  deleteDialogIconBlobSx,
  deleteDialogConfirmTextSx,
  deleteDialogDangerBtnSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'

import useSorting, {
  getSorting,
  stableSort,
} from '@/hooks/useSorting'
import { handleDownload } from '@/utils/files'

// const status = [
//   {
//     name: orgCreator,
//     color: '#4290ff',
//   },
//   {
//     name: pending,
//     color: '#FF8300',
//   },
//   {
//     name: deactivated,
//     color: '#E4000F',
//   },
//   {
//     name: active,
//     color: '#46de64',
//   },
// ]

function ManageFiles(props) {
  const [loader, setLoader] = useState(false)
  const [selectedFile, setSelectedFile] = useState('')
  const [openDelete, setOpenDelete] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const handleOpenDelete = (file) => {
    setOpenDelete(true)
    setSelectedFile(file)
    setConfirm(false)
  }
  const handleCloseDelete = () => {
    setOpenDelete(false)
    setSelectedFile(null)
    setLoader(false)
  }

  const { property } = props

  const [{ order, orderBy }, { handleRequestSort }] = useSorting({
    defaultOrder: 'asc',
    defaultOrderBy: 'createdAt',
  })

  const [page, setPage] = useState(
    parseInt(localStorage.getItem('team-page')) || 1,
  )
  const handleChange = (event, value) => {
    localStorage.setItem('team-page', value)
    setPage(value)
  }

  const toggleRemove = async (url) => {
    setLoader(true)
    const file = find(props.files, ['url', url])
    if (!file) {
      enqueueSnackbar('File not found', {
        variant: 'error',
        autoHideDuration: 3000,
      })
      setLoader(false)
      handleCloseDelete()
      return
    }
    try {
      await props.deleteFileFromList(file)
    } catch (error) {
      console.error('Error deleting file:', error)
    } finally {
      handleCloseDelete()
    }
  }

  const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  }

  const tableHeaders = props.public
    ? [
        { title: 'File Name', name: 'filename' },
        { title: 'File Type', name: 'filetype' },
      ]
    : [
        { title: 'File Name', name: 'filename' },
        { title: 'File Location', name: 'collection' },
        { title: 'File Type', name: 'filetype' },
        { title: 'Created By', name: 'createdBy' },
        { title: 'Created Date', name: 'createdAt' },
      ]

  const count = ceil(props.files.length / 20)

  return (
    <Card variant="outlined" sx={{ m: '25px' }}>
      <Box sx={[{
        display: "flex",
        alignItems: "center",
        py: "0.75rem",
        px: "1.25rem",
        mb: 0,
        backgroundColor: "#fff",
        padding: "0.75rem 1.25rem",
        marginBottom: 0,
        borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
        "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
        "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
      }, { pr: '0.5rem' }]}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <b>Files</b>
        </Box>
        <Box sx={{marginLeft:"auto",display:"flex",alignItems:"center",flexShrink:0}}>
          <FileUpload
            documentId={property._id}
            uploadFile={props.uploadFileOther}
            writeOnly
            button
            buttonColor="primary"
            buttonText="Add Files"
            buttonIcon={<Add style={strategyIconGap10Style} />}
            collection={'otherFile'}
            shared={props.shared}
          />
        </Box>
      </Box>
      <CardContent sx={{ p: 0 }}>
        <Box sx={strategyScrollXTouchSx}>
          <Table sx={[{ mb: 0 }, {
            width: "100%",
            marginBottom: "1rem",
            fontSize: "1rem",
            "& th, & td": { padding: "0.75rem", borderTop: "1px solid #e4e5f0", verticalAlign: "middle" },
            "& thead th": {
              textTransform: "uppercase",
              background: "#f4f5fd",
              fontSize: "0.875rem",
              verticalAlign: "middle",
              borderBottom: "2px solid #e4e5f0",
            },
            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
          }]}>
            <thead>
              <tr sx={{ textAlign: 'center' }}>
                {map(tableHeaders, (column) => (
                  <TableCell
                    key={column.title}
                    //style={{ whiteSpace: 'nowrap' }}
                    align={column.align}
                    sortDirection={
                      orderBy === column.name ? order : false
                    }
                  >
                    {column.name ? (
                      <TableSortLabel
                        active={orderBy === column.name}
                        direction={order}
                        onClick={(event) =>
                          handleRequestSort(event, column.name)
                        }
                      >
                        {column.title}
                        {orderBy === column.name ? (
                          <span style={visuallyHidden}>
                            {order === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.title
                    )}
                  </TableCell>
                ))}
                <TableCell>Action</TableCell>
              </tr>
            </thead>
            <tbody>
              {map(
                slice(
                  stableSort(props.files, getSorting(order, orderBy)),
                  (page - 1) * 20,
                  page * 20,
                ),
                (file) => {
                  return (
                    <tr>
                      <td>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <Box component="a" href={file.url} target="_blank" rel="noreferrer" title="..." sx={[{ fontWeight: 700 }, {
                              color: "#070919",
                              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                            }]}>
                              {file.filename}
                            </Box>
                          </div>
                        </Box>
                      </td>
                      {!props.public && (
                        <td>
                          <div>{file.collection}</div>
                        </td>
                      )}
                      <td>
                        <div>{file.filetype}</div>
                      </td>
                      {!props.public && (
                        <td>
                          <div>{file.createdBy}</div>
                        </td>
                      )}
                      {!props.public && (
                        <td>
                          <div>
                            {formatDisplayDate(file.createdAt)}
                          </div>
                        </td>
                      )}
                      <td>
                        <div>
                          {!props.shared &&
                            file.collection !== 'Plan Files' && (
                              <Tooltip title="Delete File">
                                <IconButton
                                  onClick={(event) =>
                                    handleOpenDelete(file.url)
                                  }
                                 
                                  size="large" sx={{
                                    backgroundColor:
                                      'rgba(144, 202, 249, 0.16)',
                                    height: 30,
                                    width: 30,
                                  }}>
                                  <FontAwesomeIcon icon={['far', 'circle-xmark']} sx={{ fontSize: "1.15rem !important" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          {props.public && (
                            <Tooltip title="Download File">
                              <IconButton
                                onClick={() =>
                                  handleDownload(
                                    file.url,
                                    file.filename,
                                  )
                                }
                               
                                size="large" sx={{
                                  backgroundColor:
                                    'rgba(144, 202, 249, 0.16)',
                                  height: 30,
                                  width: 30,
                                }}>
                                <FontAwesomeIcon icon={['fas', 'cloud-arrow-down']} sx={{ fontSize: "1.15rem !important" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                },
              )}
            </tbody>
          </Table>
        </Box>
        <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
        <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
        <Box
          sx={{
            p: '1rem',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination size="small" count={count} page={page} onChange={handleChange} />
        </Box>
      </CardContent>
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
      >
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={<Box sx={{ color: '#368cff' }}>Deleting File</Box>}
        >
          <Box sx={{ textAlign: 'center', p: '3rem' }}>
            <Box sx={[{ borderRadius: '50%', m: 0 }, {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mr: "0.25rem",
              position: "relative",
              fontSize: "1.2rem",
              transition: "all 0.2s ease-in-out",
              "&:last-child": { mr: 0, marginRight: 0 },
              "& .badge": { position: "absolute", right: 0, bottom: 0 },
              "& svg, & .svg-inline--fa": { display: "block" },
              marginRight: "0.25rem",
            }]}>
              <Box data-hf="delete-icon-blob" sx={deleteDialogIconBlobSx}>
                <FontAwesomeIcon icon={['far', 'trash-can']} size="10x" sx={[{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }, strategyFlexSelfCenterSx]} />
              </Box>
            </Box>
            <Box
              component="h4"
              sx={{ fontWeight: 700, mt: '1.5rem' }}
            >
              Delete File?
            </Box>
            <Box sx={deleteDialogConfirmTextSx}>
              <FormControl component="fieldset" sx={{ pr: '1.5rem' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirm}
                        onChange={() => setConfirm(!confirm)}
                      />
                    }
                    label="Are you sure you want to delete this file?"
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box sx={{ pt: '1.5rem' }}>
              <Button
                variant="text"
                onClick={handleCloseDelete}
              >
                <Box component="span" sx={{
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }}>Cancel</Box>
              </Button>
              <Button
                variant="contained"
                onClick={(event) => toggleRemove(selectedFile)}
                disabled={!confirm}
                data-hf="delete-danger-btn"
                sx={deleteDialogDangerBtnSx}
              >
                <Box component="span" sx={{
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }}>Delete</Box>
              </Button>
            </Box>
          </Box>
        </BlockUi>
      </Dialog>
    </Card>
  )
}

export default withSession(ManageFiles)
